import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import identityDisclosureService, {
  IdentityDisclosureRequestDetails
} from '../services/identityDisclosureService';

const MyIdentityVerificationRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<IdentityDisclosureRequestDetails[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await identityDisclosureService.listMyRequests();
        setRequests(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load ID verification requests');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const getStatusColor = (status: string): 'default' | 'success' | 'error' | 'warning' => {
    switch ((status || '').toUpperCase()) {
      case 'APPROVED':
        return 'success';
      case 'DENIED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5">My ID Verification Requests</Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {requests.length === 0 ? (
        <Alert severity="info">No ID verification requests yet.</Alert>
      ) : (
        requests.map((request) => (
          <Card key={request.requestId} variant="outlined">
            <CardContent>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">
                    Request #{request.requestId.slice(0, 8)}
                  </Typography>
                  <Chip size="small" label={request.status} color={getStatusColor(request.status)} />
                  <Chip size="small" label={request.isRequesterView ? 'Sent' : 'Received'} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Requester: @{request.requesterUsername} ({request.requesterDisplayName})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recipient: @{request.recipientUsername}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(request.createdAtUtc).toLocaleString()}
                </Typography>
                {request.recipientViewedAtUtc && (
                  <Typography variant="body2" color="text.secondary">
                    Viewed: {new Date(request.recipientViewedAtUtc).toLocaleString()}
                  </Typography>
                )}
                {request.decisionAtUtc && (
                  <Typography variant="body2" color="text.secondary">
                    Decided: {new Date(request.decisionAtUtc).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Fee: ${request.feeAmount.toFixed(2)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/identity-disclosure-requests/${request.requestId}`)}
                  >
                    Open Request
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );
};

export default MyIdentityVerificationRequestsPage;
